import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Loader from '../../../components/Loader';
import ApiService from '../../../services/ApiService';
import { previewImageOnClick, previewCursor } from '../../../utils/imagePreview';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';
import ProfileInfo from './ProfileInfo';
import CustomerBookingHistory from './CustomerBookingHistory';
import CustomerMemberships from './CustomerMemberships';
import EditUserModal from './EditUserModal';
import ChangePasswordModal from './ChangePasswordModal';
import './CustomerDetails.css';

const MAROON = '#4F97D8';
const CURRENCY = process.env.REACT_APP_CURRENCY || 'AED';

const TABS = [
  { key: 'profile-info',    label: 'Profile Info',    icon: 'bi-person'          },
  { key: 'booking-history', label: 'Booking History', icon: 'bi-calendar-check'  },
  { key: 'memberships',     label: 'Membership History', icon: 'bi-award'        },
];

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('profile-info');
  const [showEdit, setShowEdit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    getUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getUserDetails = async () => {
    try {
      setIsLoader(true);
      const res = await ApiService.request({ method: 'GET', url: `admin/users/${id}` });
      if (res.data.status) setUser(res.data.data);
      else { toast.error(res.data.message); navigate('/admin/users'); }
    } catch {
      toast.error('Failed to load user details');
      navigate('/admin/users');
    } finally {
      setIsLoader(false);
    }
  };

  const handleToggleStatus = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: user?.status ? 'You want to restrict this account!' : 'You want to activate this account!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: MAROON,
      confirmButtonText: 'Yes',
      cancelButtonColor: '#9E9B9B',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await ApiService.request({ method: 'POST', url: `admin/users/status/${id}` });
      if (res.data.status) { getUserDetails(); toast.success(res.data.message); }
      else toast.error(res.data.message);
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Delete this account?',
      text: 'The client will be signed out and can no longer log in. Their bookings and membership history are kept.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete',
      cancelButtonColor: '#9E9B9B',
      cancelButtonText: 'No',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await ApiService.request({ method: 'DELETE', url: `admin/users/${id}` });
      if (res.data.status) { toast.success(res.data.message); navigate('/admin/users'); }
      else toast.error(res.data.message);
    } catch {
      // ApiService already surfaced the error.
    }
  };

  const saveBookingBlock = async (blocked_until) => {
    try {
      const res = await ApiService.request({
        method: 'POST',
        url: `admin/users/${id}/booking-block`,
        data: { blocked_until },
      });
      if (res.data.status) { getUserDetails(); toast.success(res.data.message); }
      else toast.error(res.data.message);
    } catch {
      // ApiService already surfaced the error.
    }
  };

  const handleReleaseBlock = async () => {
    const result = await Swal.fire({
      title: 'Release booking block?',
      text: 'The client will be able to book again straight away.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4BC95F',
      confirmButtonText: 'Yes, release',
      cancelButtonColor: '#9E9B9B',
      cancelButtonText: 'No',
    });
    if (result.isConfirmed) saveBookingBlock(null);
  };

  const handleChangeBlock = async () => {
    const result = await Swal.fire({
      title: 'Change booking block',
      text: 'The client cannot book until this date and time.',
      input: 'datetime-local',
      inputValue: moment(user.booking_blocked_until).format('YYYY-MM-DDTHH:mm'),
      inputAttributes: { min: moment().format('YYYY-MM-DDTHH:mm') },
      inputValidator: (value) => {
        if (!value) return 'Pick a date and time';
        if (!moment(value).isAfter(moment())) return 'Block end must be in the future';
        return undefined;
      },
      showCancelButton: true,
      confirmButtonColor: MAROON,
      confirmButtonText: 'Save',
      cancelButtonColor: '#9E9B9B',
    });
    if (result.isConfirmed) saveBookingBlock(moment(result.value).toISOString());
  };

  const handleMembershipApproval = async (approval_status) => {
    const membershipId = user?.membership?.current?._id;
    if (!membershipId) return;

    const isApprove = approval_status === 'Approved';
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: isApprove
        ? 'This will charge the authorised payment and activate the membership.'
        : 'This will release the payment hold and reject the membership.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: isApprove ? '#4BC95F' : '#d33',
      confirmButtonText: isApprove ? 'Yes, approve' : 'Yes, reject',
      cancelButtonColor: '#9E9B9B',
      cancelButtonText: 'No',
    });
    if (!result.isConfirmed) return;

    try {
      const res = await ApiService.request({
        method: 'POST',
        url: `admin/membership-plans/subscribers/${membershipId}/approval`,
        data: { approval_status },
      });
      if (res.data.status) { getUserDetails(); toast.success(res.data.message); }
      else toast.error(res.data.message);
    } catch {
      toast.error('Something went wrong.');
    }
  };

  const avatarSrc = (name) =>
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=4F97D8&color=fff&size=128`;

  if (isLoader && !user) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <Loader text="Loading…" size={40} />
      </div>
    );
  }

  if (!user) return null;

  const renderContent = () => {
    switch (activeTab) {
      case 'booking-history': return <CustomerBookingHistory userId={id} />;
      case 'memberships':     return <CustomerMemberships userId={id} />;
      default:                return <ProfileInfo user={user} />;
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      {/* Breadcrumb toolbar */}
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {user.name || 'Client Details'}
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
              </li>
              <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/users" className="text-muted text-hover-primary">Clients</Link>
              </li>
              <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
              <li className="breadcrumb-item text-muted">Details</li>
            </ul>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button className="btn btn-sm btn-light-primary" onClick={() => setShowEdit(true)}>
              <i className="bi bi-pencil-square me-2"></i>Edit Account
            </button>
            <button className="btn btn-sm btn-light-primary" onClick={() => setShowPassword(true)}>
              <i className="bi bi-key me-2"></i>Change Password
            </button>
            <button className="btn btn-sm btn-light-danger" onClick={handleDelete}>
              <i className="bi bi-trash me-2"></i>Delete Account
            </button>
            <button
              className={`btn btn-sm ${user.status ? 'btn-danger' : 'btn-success'}`}
              onClick={handleToggleStatus}
            >
              <i className={`bi ${user.status ? 'bi-slash-circle-fill' : 'bi-check-circle-fill'} me-2`}></i>
              {user.status ? 'Restrict Account' : 'Activate Account'}
            </button>
            <Link to="/admin/users" className="btn btn-sm btn-light">
              <i className="bi bi-arrow-left me-2"></i>Back
            </Link>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          {user.booking_blocked_until && new Date(user.booking_blocked_until) > new Date() && (
            <div className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-3 mb-5">
              <div className="d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill fs-3 me-3"></i>
                <span>
                  Booking blocked until{' '}
                  <strong>{moment(user.booking_blocked_until).format('DD MMM YYYY, hh:mm A')}</strong>{' '}
                  due to a late subscription cancellation.
                </span>
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-light-primary" onClick={handleChangeBlock}>
                  <i className="bi bi-calendar-event me-1"></i>Change
                </button>
                <button className="btn btn-sm btn-light-success" onClick={handleReleaseBlock}>
                  <i className="bi bi-unlock me-1"></i>Release
                </button>
              </div>
            </div>
          )}

          {user.membership?.current?.approval_status === 'Pending' && (
            <div className="alert alert-warning text-dark d-flex align-items-center justify-content-between flex-wrap gap-3 mb-5">
              <div className="d-flex align-items-center">
                <i className="bi bi-award fs-3 me-3"></i>
                <span>
                  Membership request for <strong>{user.membership.current.plan_title || 'a plan'}</strong> is awaiting approval.
                </span>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-light-success"
                  onClick={() => handleMembershipApproval('Approved')}
                >
                  <i className="bi bi-check-lg me-1"></i>Approve
                </button>
                <button
                  className="btn btn-sm btn-light-danger"
                  onClick={() => handleMembershipApproval('Rejected')}
                >
                  <i className="bi bi-x-lg me-1"></i>Reject
                </button>
              </div>
            </div>
          )}

          <div className="customer-detail-layout">

            {/* ── Left Sidebar ── */}
            <div className="customer-detail-sidebar">
              {/* Profile card */}
              <div className="customer-sidebar-profile">
                <div className="customer-sidebar-cover">
                  <div className="customer-sidebar-avatar">
                    <img
                      src={user.picture || avatarSrc(user.name)}
                      alt={user.name}
                      style={{ cursor: previewCursor(user.picture) }}
                      onClick={(e) => previewImageOnClick(e, user.name)}
                      onError={(e) => { e.target.src = avatarSrc(user.name); }}
                    />
                  </div>
                </div>
                <div className="customer-sidebar-info">
                  <div className="customer-sidebar-name">{user.name || 'N/A'}</div>
                  <div className="customer-sidebar-email">{user.email || ''}</div>
                  <span className={user.status ? 'user-badge-active' : 'user-badge-restricted'}>
                    {user.status ? 'Active' : 'Restricted'}
                  </span>
                </div>
              </div>

              {/* Quick facts */}
              <div className="customer-sidebar-facts">
                <div className="customer-sidebar-fact">
                  <span className="customer-sidebar-fact-label"><i className="bi bi-telephone"></i>Phone</span>
                  <span className="customer-sidebar-fact-value">{user.phone || 'N/A'}</span>
                </div>
                <div className="customer-sidebar-fact">
                  <span className="customer-sidebar-fact-label"><i className="bi bi-patch-check"></i>Verified</span>
                  <span className="customer-sidebar-fact-value">
                    {user.phone_verified_at
                      ? <span className="badge badge-light-success">Yes</span>
                      : <span className="badge badge-light-danger">No</span>}
                  </span>
                </div>
                <div className="customer-sidebar-fact">
                  <span className="customer-sidebar-fact-label"><i className="bi bi-award"></i>Membership</span>
                  <span className="customer-sidebar-fact-value">
                    {user.membership?.current?.plan_title || '—'}
                  </span>
                </div>
                <div className="customer-sidebar-fact">
                  <span className="customer-sidebar-fact-label"><i className="bi bi-calendar-check"></i>Bookings</span>
                  <span className="customer-sidebar-fact-value">{user.booking_stats?.total_bookings ?? 0}</span>
                </div>
                <div className="customer-sidebar-fact">
                  <span className="customer-sidebar-fact-label"><i className="bi bi-cash-coin"></i>Spent</span>
                  <span className="customer-sidebar-fact-value">
                    {CURRENCY} {parseFloat(user.booking_stats?.total_spent || 0).toFixed(2)}
                  </span>
                </div>
                <div className="customer-sidebar-fact">
                  <span className="customer-sidebar-fact-label"><i className="bi bi-clock-history"></i>Member since</span>
                  <span className="customer-sidebar-fact-value">
                    {user.createdAt ? moment(user.createdAt).format('DD MMM YYYY') : 'N/A'}
                  </span>
                </div>
              </div>

              {/* Tab nav */}
              <div className="customer-sidebar-nav">
                {TABS.map(({ key, label, icon }) => (
                  <button
                    key={key}
                    className={`customer-sidebar-nav-item ${activeTab === key ? 'active' : ''}`}
                    onClick={() => setActiveTab(key)}
                  >
                    <i className={`bi ${icon}`}></i>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Right Content ── */}
            <div className="customer-detail-content">
              {renderContent()}
            </div>

          </div>
        </div>
      </div>

      <EditUserModal show={showEdit} user={user} onHide={() => setShowEdit(false)} onSaved={getUserDetails} />
      <ChangePasswordModal show={showPassword} user={user} onHide={() => setShowPassword(false)} />
    </div>
  );
};

export default CustomerDetails;
